import { DroneEntity, DroneStatusType } from '@src/core/features/drone/entities';
import {
  NOTIFICATION_CONNECTIVITY_TYPES,
  NOTIFICATION_FIRMWARE_TYPES,
  NOTIFICATION_MISSION_TYPES,
  NotificationEntity,
} from '@src/core/features/notifications/entities';
import { routesConfig } from '@src/core/routes';
import { trustedMissionIdFromDrone } from '@src/features/drone/utils/trusted-mission-id-from-drone';
import { DRONE_ID_QUERY_KEY } from '@src/features/fleet/pages/drones-page/constants';

const LIVE_DRONE_TYPES = [...NOTIFICATION_MISSION_TYPES, ...NOTIFICATION_CONNECTIVITY_TYPES];

export interface NotificationTarget {
  url: string;
  /** Preselect this drone so live ops renders it before the drones list resolves. */
  drone?: DroneEntity;
}

/** GET /drone can return an IDLE and a flying row for one serial; the flying one wins. */
const findNotificationDrone = (
  drones: DroneEntity[],
  notification: NotificationEntity,
): DroneEntity | undefined => {
  const byId = notification.droneId != null ? drones.filter((d) => d.id === notification.droneId) : [];
  const rows = byId.length > 0
    ? byId
    : drones.filter((d) => Boolean(notification.droneSerial) && d.serialNumber === notification.droneSerial);

  return rows.find((d) => d.status !== DroneStatusType.IDLE) ?? rows[0];
};

const isOnMission = (drone: DroneEntity): boolean =>
  drone.status === DroneStatusType.MISSION ||
  (drone.currentOrderId != null && Number(drone.currentOrderId) !== 0);

const liveDroneSearch = (drone: DroneEntity, notification: NotificationEntity): string => {
  const params = new URLSearchParams({ droneSerial: drone.serialNumber });

  const missionId = trustedMissionIdFromDrone(drone);
  if (missionId) params.set('missionId', missionId);

  const orderId = drone.currentOrderId ?? notification.orderId;
  if (orderId != null && orderId !== '') params.set('orderId', String(orderId));

  return params.toString();
};

/**
 * Where a notification takes you: firmware ones open the drone on Fleet, mission and connectivity
 * ones open live ops — and only while the drone is actually on a mission. Standing on the target
 * page is a no-op, hence `null`.
 */
export const resolveNotificationTarget = (
  notification: NotificationEntity,
  drones: DroneEntity[],
  pathname: string,
): NotificationTarget | null => {
  if (NOTIFICATION_FIRMWARE_TYPES.includes(notification.type)) {
    if (notification.droneId == null || pathname.startsWith(routesConfig.fleet)) return null;

    return { url: `${routesConfig.fleet}?${DRONE_ID_QUERY_KEY}=${notification.droneId}` };
  }

  if (!LIVE_DRONE_TYPES.includes(notification.type)) return null;
  if (pathname.startsWith(routesConfig.drones)) return null;

  const drone = findNotificationDrone(drones, notification);
  if (!drone || !isOnMission(drone)) return null;

  return { url: `${routesConfig.drones}?${liveDroneSearch(drone, notification)}`, drone };
};
